import type { Skill } from './content/types.ts';
import { advance, dayOf, progress, spend, type LifeEvent, type LifeState, type NeedKey } from './life.ts';
import { homeStyleOf } from './housing.ts';
import { pantryOf, groceryById, useKit } from './groceries.ts';
import { dishById, type DishId } from './dishes.ts';
import { localPrice } from './character.ts';

/**
 * Interactive activities: the player does the thing (plays the keyboard, cooks, picks an outfit)
 * and how well they do feeds the simulation. Every activity is an ActivitySession from day one,
 * with a participant list and shared state, so a second person (a duet, a friend cooking with
 * you) can join later without rebuilding the activity.
 */

export type ActivityKind = 'music' | 'cooking' | 'wardrobe' | 'painting' | 'gaming' | 'pool';

export interface Participant { who: 'player' | string; role: string }

export interface ActivitySession {
  kind: ActivityKind;
  participants: Participant[];
  startedAt: number;
  /** activity-specific state both participants would see (score, step, chosen outfit…) */
  shared: Record<string, unknown>;
}

export interface ActivityDef {
  kind: ActivityKind;
  label: string;
  /** what it costs to start (ingredients etc.) */
  cost: number;
  /** something you must own */
  requires?: string;
  /** typical game minutes */
  minutes: number;
}

export const ACTIVITIES: Record<ActivityKind, ActivityDef> = {
  music: { kind: 'music', label: 'Play the keyboard', cost: 0, requires: 'keyboard', minutes: 45 },
  cooking: { kind: 'cooking', label: 'Cook', cost: 2_800, minutes: 75 },
  wardrobe: { kind: 'wardrobe', label: 'Get dressed', cost: 0, minutes: 15 },
  painting: { kind: 'painting', label: 'Paint', cost: 0, requires: 'easel', minutes: 60 },
  gaming: { kind: 'gaming', label: 'Play on the console', cost: 0, requires: 'console', minutes: 40 },
  pool: { kind: 'pool', label: 'A game of pool', cost: 0, requires: 'pool', minutes: 30 },
};

/** `provided`: the place supplies what it needs (a sip and paint: the studio's easel, canvas and paints) */
export function canStart(s: LifeState, kind: ActivityKind, dish?: DishId, provided = false): string | null {
  const d = ACTIVITIES[kind];
  if (provided) return null;
  if (d.requires === 'easel' || d.requires === 'console' || d.requires === 'pool') { if (!homeStyleOf(s).extras.includes(d.requires)) return d.requires === 'easel' ? 'You need the art pack (Customise: easel, canvases, paints).' : d.requires === 'console' ? 'You need a games console (Customise).' : 'You need a pool table (Customise).'; }
  else if (d.requires && !s.owned.includes(d.requires)) return `You don't have a ${d.requires} yet.`;
  const cost = kind === 'cooking' ? localPrice(s, dishById(dish).ngn, 100) : d.cost;
  if (cost > s.money && !(kind === 'cooking' && (dish ?? 'jollof') === 'jollof' && Object.keys(pantryOf(s)).some((k) => groceryById(k)?.kit === 'jollof'))) return "You can't afford the ingredients.";
  return null;
}

export function startActivity(s: LifeState, kind: ActivityKind, dish?: DishId, provided = false): ActivitySession | null {
  if (canStart(s, kind, dish, provided)) return null;
  const d = ACTIVITIES[kind];
  if (provided) { /* (paid for with the ticket) */ } else if (kind === 'cooking') { if ((dish ?? 'jollof') !== 'jollof' || !useKit(s, 'jollof')) spend(s, localPrice(s, dishById(dish).ngn, 100), 'Ingredients'); } // (a jollof kit from the supermarket covers it)
  else if (d.cost) spend(s, d.cost, d.label);
  return { kind, participants: [{ who: 'player', role: kind === 'music' ? 'keys' : kind === 'cooking' ? 'cook' : kind === 'painting' ? 'painter' : kind === 'gaming' || kind === 'pool' ? 'player' : 'self' }], startedAt: s.minutes, shared: dish ? { dish } : {} };
}

const clamp = (v: number) => Math.max(0, Math.min(100, v));
/** Practice pays more when you're new to something; experts improve slowly. */
const learn = (s: LifeState, k: Skill, amount: number) => { s.skills[k] = Math.min(100, s.skills[k] + amount * (1 - s.skills[k] / 120)); };

/**
 * End an activity with a performance from 0 (a mess) to 1 (perfect). Returns what happened, in
 * plain lines for the end-of-scene card.
 */
export function finishActivity(s: LifeState, a: ActivitySession, performance: number, minutes = ACTIVITIES[a.kind].minutes): { events: LifeEvent[]; lines: string[] } {
  const p = Math.max(0, Math.min(1, performance));
  const lines: string[] = [];
  const need = (k: NeedKey, v: number) => { s.needs[k] = clamp(s.needs[k] + v); };
  const before = { ...s.skills };
  const events: LifeEvent[] = [];
  const first = !s.history.some((h) => h.text.startsWith(firstLine[a.kind]));
  switch (a.kind) {
    case 'music': {
      learn(s, 'music', 0.8 + p * 2.2);
      learn(s, 'creativity', 0.3 + p * 0.6);
      need('fun', 10 + p * 22);
      need('energy', -4);
      lines.push(p > 0.85 ? 'That was clean. You could play this in front of people.' : p > 0.6 ? 'A few slips, but it\'s coming together.' : p > 0.3 ? 'Rough. Your fingers know more than they did this morning.' : 'It fell apart. Everyone starts somewhere.');
      events.push(...progress(s, 'practice'));
      break;
    }
    case 'cooking': {
      const burnt = !!a.shared.burnt, dish = dishById(a.shared.dish as string | undefined);
      need('hunger', burnt ? 22 : Math.round(dish.hunger * (0.45 + p * 0.55)));
      need('fun', 4 + p * 8);
      learn(s, 'cooking', 0.6 + p * 2);
      lines.push(burnt ? dish.lines[3] : p > 0.85 ? dish.lines[0] : p > 0.55 ? dish.lines[1] : dish.lines[2]);
      if (!burnt && p > 0.7) lines.push('There\'s enough left over for tomorrow.');
      events.push(...progress(s, 'cook'));
      break;
    }
    case 'painting': {
      learn(s, 'creativity', 0.8 + p * 1.8);
      need('fun', 12 + p * 18);
      need('energy', -3);
      lines.push(p > 0.8 ? "You step back from the easel. It's good. Properly good." : p > 0.5 ? "Not what you pictured, but there's something in it." : p > 0.2 ? 'A start. The colours fought you a bit.' : "Mostly bare canvas. Some days the brush doesn't want to.");
      break;
    }
    case 'gaming': {
      need('fun', 10 + p * 16); need('energy', -3);
      lines.push(p > 0.7 ? 'You could go pro. (You could not.)' : p > 0.4 ? 'A decent session. The keeper read you once or twice.' : 'The pad is clearly broken.');
      break;
    }
    case 'pool': {
      need('fun', 10 + p * 12);
      learn(s, 'technical', 0.2 + p * 0.4);
      lines.push(p > 0.7 ? 'Clean break, three in a row, the black to finish. Lovely.' : p > 0.4 ? 'A few good pots, a few that rattled in the jaws.' : 'You mostly moved the white ball around the table.');
      break;
    }
    case 'wardrobe': {
      const outfit = a.shared.outfit as string[] | undefined;
      if (outfit) s.outfit = outfit;
      s.dressedDay = dayOf(s.minutes);
      need('fun', 4);
      lines.push('You look like you have somewhere to be. People will notice today.');
      break;
    }
  }
  if (first) s.history.push({ t: s.minutes, text: firstLine[a.kind] });
  events.push(...advance(s, minutes));
  for (const k of Object.keys(s.skills) as Skill[]) {
    const gained = s.skills[k] - before[k];
    if (gained >= 0.5) lines.push(`${k[0].toUpperCase()}${k.slice(1)} ${Math.round(before[k])} → ${Math.round(s.skills[k])}`);
  }
  return { events, lines };
}

const firstLine: Record<ActivityKind, string> = {
  music: 'Played the keyboard',
  cooking: 'Cooked a meal at home',
  wardrobe: 'Put together a look',
  painting: 'Painted a picture',
  gaming: 'Played on the console',
  pool: 'Played pool at home',
};

// --- paintings you made: hung on your walls (a few at a time) or kept in the archive ---------------------------

export interface Painting { id: string; title: string; t: number; hung: boolean }
/** how many fit on your walls at once */
export const WALL_SPACE = 3;
export const paintingsOf = (s: LifeState): Painting[] => (s.paintings ??= []);
/** Keep a new painting (its picture is stored by the game under `id`). Hanging it when the walls are full takes the oldest down. */
export function keepPainting(s: LifeState, id: string, title: string, hang: boolean) {
  paintingsOf(s).push({ id, title: title.trim() || 'Untitled', t: s.minutes, hung: false });
  if (hang) hangPainting(s, id);
}
export function hangPainting(s: LifeState, id: string) {
  const all = paintingsOf(s), p = all.find((x) => x.id === id);
  if (!p) return;
  const up = all.filter((x) => x.hung).sort((a, b) => a.t - b.t);
  if (up.length >= WALL_SPACE) up[0].hung = false;
  p.hung = true;
}
export function takeDownPainting(s: LifeState, id: string) { const p = paintingsOf(s).find((x) => x.id === id); if (p) p.hung = false; }
